import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { adminTunesService, TuneWithAnalysis, SimilarTune } from '@/services/adminTunesService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw, Search, Eye, Music } from 'lucide-react';

const TuneSearch: React.FC = () => {
  const [tunes, setTunes] = useState<TuneWithAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedTune, setSelectedTune] = useState<TuneWithAnalysis | null>(null);
  const [similarTunes, setSimilarTunes] = useState<SimilarTune[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const pageSize = 25;

  // Filter states
  const [bpmMin, setBpmMin] = useState('');
  const [bpmMax, setBpmMax] = useState('');
  const [key, setKey] = useState('');
  const [scale, setScale] = useState('');
  const [loudnessMin, setLoudnessMin] = useState('');
  const [loudnessMax, setLoudnessMax] = useState('');
  const [genre, setGenre] = useState('');
  const [artist, setArtist] = useState('');
  const [year, setYear] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [durationMax, setDurationMax] = useState('');
  const [hasFingerprint, setHasFingerprint] = useState<string>('all');
  const [hasChromaVector, setHasChromaVector] = useState<string>('all');
  const [extractionStatus, setExtractionStatus] = useState('');
  const [userId, setUserId] = useState('');

  const fetchTunes = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        offset: currentPage * pageSize
      };

      if (bpmMin) params.bpm_min = parseFloat(bpmMin);
      if (bpmMax) params.bpm_max = parseFloat(bpmMax);
      if (key) params.key = key;
      if (scale) params.scale = scale;
      if (loudnessMin) params.loudness_min = parseFloat(loudnessMin);
      if (loudnessMax) params.loudness_max = parseFloat(loudnessMax);
      if (genre) params.genre = genre;
      if (artist) params.artist = artist;
      if (year) params.year = year;
      if (durationMin) params.duration_min = parseFloat(durationMin);
      if (durationMax) params.duration_max = parseFloat(durationMax);
      if (hasFingerprint !== 'all') params.has_fingerprint = hasFingerprint === 'true';
      if (hasChromaVector !== 'all') params.has_chroma_vector = hasChromaVector === 'true';
      if (extractionStatus) params.extraction_status = extractionStatus;
      if (userId) params.user_id = userId;

      const data = await adminTunesService.searchTunes(params);
      setTunes(data.tunes);
      setTotalCount(data.total);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch tunes');
    } finally {
      setLoading(false);
    }
  }, [
    currentPage, bpmMin, bpmMax, key, scale, loudnessMin, loudnessMax,
    genre, artist, year, durationMin, durationMax,
    hasFingerprint, hasChromaVector, extractionStatus, userId
  ]);

  useEffect(() => {
    fetchTunes();
  }, [fetchTunes]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [
    bpmMin, bpmMax, key, scale, loudnessMin, loudnessMax,
    genre, artist, year, durationMin, durationMax,
    hasFingerprint, hasChromaVector, extractionStatus, userId
  ]);

  const handleViewDetails = (tune: TuneWithAnalysis) => {
    setSelectedTune(tune);
    setIsDetailModalOpen(true);
  };

  const handleViewSimilar = async (tune: TuneWithAnalysis) => {
    try {
      const data = await adminTunesService.findSimilarTunes(tune.id, {
        limit: 10,
        min_similarity: 0.5
      });
      setSimilarTunes(data.similar_tunes);
      setSelectedTune(tune);
      setIsSimilarModalOpen(true);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch similar tunes');
    }
  };

  const handleClearFilters = () => {
    setBpmMin('');
    setBpmMax('');
    setKey('');
    setScale('');
    setLoudnessMin('');
    setLoudnessMax('');
    setGenre('');
    setArtist('');
    setYear('');
    setDurationMin('');
    setDurationMax('');
    setHasFingerprint('all');
    setHasChromaVector('all');
    setExtractionStatus('');
    setUserId('');
    setCurrentPage(0);
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      processing: 'secondary',
      pending: 'outline',
      failed: 'destructive'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle>Search Filters</CardTitle>
          <CardDescription>Filter tunes by audio analysis metadata</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* BPM Range */}
            <div className="space-y-2">
              <Label>BPM Range</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="140"
                  value={bpmMin}
                  onChange={(e) => setBpmMin(e.target.value)}
                  className="w-full"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="number"
                  placeholder="160"
                  value={bpmMax}
                  onChange={(e) => setBpmMax(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Loudness Range */}
            <div className="space-y-2">
              <Label>Loudness (LUFS)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="-14"
                  value={loudnessMin}
                  onChange={(e) => setLoudnessMin(e.target.value)}
                  className="w-full"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="number"
                  placeholder="-8"
                  value={loudnessMax}
                  onChange={(e) => setLoudnessMax(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Duration Range */}
            <div className="space-y-2">
              <Label>Duration (sec)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="120"
                  value={durationMin}
                  onChange={(e) => setDurationMin(e.target.value)}
                  className="w-full"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="number"
                  placeholder="300"
                  value={durationMax}
                  onChange={(e) => setDurationMax(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Key/Scale */}
            <div className="space-y-2">
              <Label htmlFor="key">Musical Key</Label>
              <Input
                id="key"
                placeholder="e.g. A, C#, Bb"
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scale">Scale</Label>
              <Select value={scale} onValueChange={setScale}>
                <SelectTrigger>
                  <SelectValue placeholder="All scales" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="major">Major</SelectItem>
                  <SelectItem value="minor">Minor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ID3 Tags */}
            <div className="space-y-2">
              <Label htmlFor="genre">Genre</Label>
              <Input
                id="genre"
                placeholder="e.g. Detroit Trap Beat"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="artist">Artist</Label>
              <Input
                id="artist"
                placeholder="e.g. Metro Boomin"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                placeholder="e.g. 2024"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>

            {/* Flags */}
            <div className="space-y-2">
              <Label htmlFor="has-fingerprint">Has Fingerprint</Label>
              <Select value={hasFingerprint} onValueChange={setHasFingerprint}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="has-chroma">Has Chroma Vector</Label>
              <Select value={hasChromaVector} onValueChange={setHasChromaVector}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="extraction-status">Extraction Status</Label>
              <Select value={extractionStatus} onValueChange={setExtractionStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-id">User ID</Label>
              <Input
                id="user-id"
                placeholder="Filter by user"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={fetchTunes} disabled={loading} size="sm">
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters} size="sm">
              Clear Filters
            </Button>
            <Button variant="outline" onClick={fetchTunes} disabled={loading} size="sm">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Display */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading tunes...</p>
          </CardContent>
        </Card>
      ) : tunes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Music className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No tunes found</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">Tunes ({totalCount} total)</h3>
              <p className="text-sm text-muted-foreground">
                Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)}
              </p>
            </div>
            {tunes.map((tune) => (
              <Card key={tune.id} className="hover:bg-muted/50 transition-colors">
                <CardContent className="p-4 space-y-3">
                  {/* Title and Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm line-clamp-2">{tune.video_title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">ID: {tune.id}</p>
                    </div>
                    {tune.audio_analysis
                      ? getStatusBadge(tune.audio_analysis.extraction_status)
                      : <Badge variant="outline" className="text-xs flex-shrink-0">No Analysis</Badge>}
                  </div>

                  {/* User and YouTube ID */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">User Email</p>
                      <p className="truncate text-xs">{tune.user_email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">YouTube ID</p>
                      <p className="font-mono text-xs truncate">{tune.youtube_id || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Audio Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">BPM</p>
                      <p className="font-medium">
                        {tune.audio_analysis?.detected_bpm
                          ? `${Math.round(tune.audio_analysis.detected_bpm)} BPM`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Duration</p>
                      <p className="font-medium">{formatDuration(tune.audio_analysis?.duration || null)}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(tune)}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                    {tune.audio_analysis?.chroma_vector && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewSimilar(tune)}
                        title="Find similar tunes"
                      >
                        <Music className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Mobile Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
              <span className="text-sm text-muted-foreground">
                Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={(currentPage + 1) * pageSize >= totalCount}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>

          {/* Desktop Table View */}
          <Card className="hidden lg:block">
            <CardHeader>
              <CardTitle>Tunes ({totalCount} total)</CardTitle>
              <CardDescription>
                Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>User Email</TableHead>
                      <TableHead>YouTube ID</TableHead>
                      <TableHead>BPM</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tunes.map((tune) => (
                      <TableRow key={tune.id}>
                        <TableCell>{tune.id}</TableCell>
                        <TableCell className="max-w-xs truncate">{tune.video_title}</TableCell>
                        <TableCell className="text-sm">{tune.user_email}</TableCell>
                        <TableCell className="font-mono text-xs">{tune.youtube_id || 'N/A'}</TableCell>
                        <TableCell>
                          {tune.audio_analysis?.detected_bpm
                            ? `${Math.round(tune.audio_analysis.detected_bpm)} BPM`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{formatDuration(tune.audio_analysis?.duration || null)}</TableCell>
                        <TableCell>
                          {tune.audio_analysis
                            ? getStatusBadge(tune.audio_analysis.extraction_status)
                            : <Badge variant="outline">No Analysis</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetails(tune)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {tune.audio_analysis?.chroma_vector && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewSimilar(tune)}
                                title="Find similar tunes"
                              >
                                <Music className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Desktop Pagination */}
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={(currentPage + 1) * pageSize >= totalCount}
                >
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTune?.video_title}</DialogTitle>
            <DialogDescription>Audio Analysis Details</DialogDescription>
          </DialogHeader>
          {selectedTune && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Tune Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">ID:</span> {selectedTune.id}</div>
                  <div><span className="text-muted-foreground">User ID:</span> {selectedTune.user_id}</div>
                  <div><span className="text-muted-foreground">User Email:</span> {selectedTune.user_email}</div>
                  <div><span className="text-muted-foreground">YouTube ID:</span> {selectedTune.youtube_id || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Privacy:</span> {selectedTune.privacy_status}</div>
                </div>
              </div>

              {selectedTune.audio_analysis && (
                <>
                  <div>
                    <h3 className="font-semibold mb-2">Technical Metadata</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Duration:</span> {formatDuration(selectedTune.audio_analysis.duration)}</div>
                      <div><span className="text-muted-foreground">Bitrate:</span> {selectedTune.audio_analysis.bitrate || 'N/A'} kbps</div>
                      <div><span className="text-muted-foreground">Sample Rate:</span> {selectedTune.audio_analysis.sample_rate || 'N/A'} Hz</div>
                      <div><span className="text-muted-foreground">Channels:</span> {selectedTune.audio_analysis.channels || 'N/A'}</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Audio Features</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">BPM:</span> {selectedTune.audio_analysis.detected_bpm?.toFixed(1) || 'N/A'}</div>
                      <div><span className="text-muted-foreground">BPM Confidence:</span> {selectedTune.audio_analysis.bpm_confidence?.toFixed(2) || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Key:</span> {selectedTune.audio_analysis.detected_key || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Scale:</span> {selectedTune.audio_analysis.detected_scale || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Key Confidence:</span> {selectedTune.audio_analysis.key_confidence?.toFixed(2) || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Loudness:</span> {selectedTune.audio_analysis.loudness_integrated?.toFixed(1) || 'N/A'} LUFS</div>
                      <div><span className="text-muted-foreground">Dynamic Range:</span> {selectedTune.audio_analysis.dynamic_range?.toFixed(1) || 'N/A'} dB</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">ID3 Tags</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Artist:</span> {selectedTune.audio_analysis.tag_artist || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Genre:</span> {selectedTune.audio_analysis.tag_genre || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Year:</span> {selectedTune.audio_analysis.tag_year || 'N/A'}</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Fingerprints</h3>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Chromaprint:</span>{' '}
                        {selectedTune.audio_analysis.chromaprint_fingerprint ? (
                          <span className="font-mono text-xs">
                            {selectedTune.audio_analysis.chromaprint_fingerprint.substring(0, 50)}...
                          </span>
                        ) : 'N/A'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Chroma Vector:</span>{' '}
                        {selectedTune.audio_analysis.chroma_vector ? 'Available' : 'N/A'}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {selectedTune.benchmark_ffmpeg_time && (
                <div>
                  <h3 className="font-semibold mb-2">Performance Metrics</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">FFmpeg Time:</span> {selectedTune.benchmark_ffmpeg_time.toFixed(2)}s</div>
                    <div><span className="text-muted-foreground">Upload Time:</span> {selectedTune.benchmark_upload_time?.toFixed(2) || 'N/A'}s</div>
                    <div><span className="text-muted-foreground">Total Time:</span> {selectedTune.benchmark_total_time?.toFixed(2) || 'N/A'}s</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Similar Tunes Modal */}
      <Dialog open={isSimilarModalOpen} onOpenChange={setIsSimilarModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Similar Tunes to "{selectedTune?.video_title}"</DialogTitle>
            <DialogDescription>Based on chroma vector cosine similarity</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {similarTunes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No similar tunes found</p>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="lg:hidden space-y-3">
                  {similarTunes.map((tune) => (
                    <Card key={tune.id} className="hover:bg-muted/50 transition-colors">
                      <CardContent className="p-4 space-y-3">
                        {/* Title and Similarity */}
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-sm line-clamp-2 flex-1">{tune.video_title}</h4>
                          <Badge variant={tune.similarity_score > 0.8 ? 'default' : 'secondary'} className="flex-shrink-0">
                            {(tune.similarity_score * 100).toFixed(1)}%
                          </Badge>
                        </div>

                        {/* Audio Info Grid */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">BPM</p>
                            <p className="font-medium">
                              {tune.detected_bpm ? `${Math.round(tune.detected_bpm)} BPM` : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Key</p>
                            <p className="font-medium">
                              {tune.detected_key && tune.detected_scale
                                ? `${tune.detected_key} ${tune.detected_scale}`
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground mb-1">Genre</p>
                            <p className="font-medium">{tune.tag_genre || 'N/A'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Similarity</TableHead>
                        <TableHead>BPM</TableHead>
                        <TableHead>Key</TableHead>
                        <TableHead>Genre</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {similarTunes.map((tune) => (
                        <TableRow key={tune.id}>
                          <TableCell className="max-w-xs truncate">{tune.video_title}</TableCell>
                          <TableCell>
                            <Badge variant={tune.similarity_score > 0.8 ? 'default' : 'secondary'}>
                              {(tune.similarity_score * 100).toFixed(1)}%
                            </Badge>
                          </TableCell>
                          <TableCell>{tune.detected_bpm ? `${Math.round(tune.detected_bpm)} BPM` : 'N/A'}</TableCell>
                          <TableCell>
                            {tune.detected_key && tune.detected_scale
                              ? `${tune.detected_key} ${tune.detected_scale}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{tune.tag_genre || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TuneSearch;
